<script>
    import { goto } from "$app/navigation";
    import { fly, fade, blur } from 'svelte/transition';
    import { cubicOut, circOut, expoOut } from 'svelte/easing';
    import FancyButton from "$components/fancyButton.svelte";
    import TextButton from "$components/textButton.svelte";
    import ToggleSwitch from "$components/toggleSwitch.svelte";
    import Transparent from "$images/clear-folder.svg";
    import ClearFolder from "$components/clearFolder.svelte";
    import Blobs from "$components/blobs.svelte";
	import { viewport } from "$stores/shared.svelte";

    let month = $state(
         new Date().toLocaleString('en-US', { month: 'short' })
        );

    let year = $state(
        new Date().toLocaleString('en-US', { year: '2-digit' })
        );

    const sectionLabels = [
        "The problem",
        "Our solution",
        "The result",
        "How we work",
        "Our track record",
        "Compare",
        "Case studies",
        "Next steps",
    ]

    const sectionTitles = [
        "Execution is hard, we make it easy.",
        "Unlike other firms, we own the entire process end-to-end.",
        "You launch a MVP product with a higher chance of succeeding.",
        "Transparent, aligned, and accountable at every step.",
        "Outcomes that speak volumes.",
        "There's them and there's us.",
        "Product overhaul ready to pitch to customer leads.",
        "Ready to make it real?",

    ]

    const cards = [
        {
            title: "Scenario A",
            content: "You hire a dev shop only to end up a year later with an overbuilt mess and features customers don't want.",
        },
        {
            title: "Scenario B",
            content: "A design agency delivered pretty screens that developers struggle to translate.",
        },
        {
            title: "Scenario C",
            content: "You have a vision but no roadmap to make it real.",
        }
    ]

    const flyIn = {y: 200 , delay: 2500,  duration: 500 , opacity: 0, easing: circOut}


</script>

<!-- 1 Hook -->
<section class="first">
    <h1 class="first" in:blur|global={{ delay: 500, duration: 1000 , amount: 220, opacity: .1, easing: cubicOut }}>
        Build software that matters.
    </h1>
    <h2 class="first" in:fly|global={{ delay: 1650, duration: 600, y: 120, opacity: 0, easing: cubicOut }}>
        By understanding the user and ditching shiny trends, we minimize risk and maximize impact.</h2>
</section>

<!-- 2 Problem -->
<section in:fly|global={flyIn}>
<span class="text-xs dark">{sectionLabels[0]}</span>
<h1 class="s2">{sectionTitles[0]}</h1>

<div class="multi cards f-row responsive">
    {#each cards as card, i}
        <div class="card" in:fly|global={{ delay: 3200 + i * 100 , duration: 500, x: 200, opacity: 0, circOut }}>
            <span class="dark">{card.title}</span>
            <p>{card.content}</p>
        </div>
    {/each}
</div>

<Blobs />

</section>

<!-- 3 Solution -->
<section class="f-col">
<span class="text-xs dark">{sectionLabels[1]}</span>
<h1 class="s3" in:fly={flyIn}>
    {sectionTitles[1]}
</h1>

<div class="subsection f-row responsive align-fs botPad">
    {#if viewport.vw > 768}
        <div class="text-xs"><!-- for spacing --></div>
    {/if}
    <p>
        We blend the best principles from human-centered design and systems engineering together.
        On top of an integrated approach, continuous improvement is our competitive edge. Every product shipped results in increased efficiency; we get faster, and more robust over time.
    </p>
</div>
<section class="subsection f-row responsive align-fs">
    <span class="text-xs dark">Our service stack</span>
    
    <div class="s3 multi f-row responsive">
        <div class="f-col gap-8">
            <span class="light">Branding</span>
            <p class="wide dark">
                Visual identity to match your brand vision, differentiated from competitors, and a logo with accompanying visual assets that proudly stand the test of time.
            </p>
        </div>
        <div class="f-col gap-8">
            <span class="light">Product Design</span>
            <p class="wide dark">
                User experience and interface (UX/UI) design. Build momentum with iterative testing, evaluation, and revision cycles until you achieve product market fit.
            </p>
        </div>
        <div class="f-col gap-8">
            <span class="light">Website Optimization</span>
            <p class="wide dark">
                Go to market with a website that connects with your target audience, featuring tight copywriting and visuals that convert traffic into sales.
            </p>
        </div>
        <div class="f-col gap-8">
            <span class="light">Full Stack Development</span>
            <p class="wide dark">
                Converting concepts, mockups, prototypes into a scalable MVP ready for investor demos or customer sign-ups. So you launch with confidence.
            </p>
        </div>
    </div>
</section>
</section>

<!-- 4 Result -->
<section>
<span class="text-xs dark">{sectionLabels[2]}</span>
<h1 class="s4">{sectionTitles[2]}</h1>

<!-- <div class="f-row responsive gap-28 pady-24">
    <div class="placeholder"></div>
    <div class="placeholder"></div>
    <div class="placeholder"></div>
</div>
<div class="placeholder"></div>
<div class="f-row responsive gap-28 pady-24">
    <div class="placeholder"></div>
    <div class="placeholder"></div>
    <div class="placeholder"></div>
</div> -->
</section>

<!-- 5 How we work -->
<section class="f-row responsive">
    <div class="f-col">
        <span class="text-xs dark">{sectionLabels[3]}</span>
        <h1 class="s5">{sectionTitles[3]}</h1>
            
        {#if viewport.vw < 821}
            <ClearFolder />
        {/if}

        <section class="subsection f-row responsive botPad">
            <span class="text-xs dark">In practice, that means</span>
        
            <div class="s5 multi f-row responsive">
                <div class="w-100p f-col gap-8">
                    <span>Clarity</span>
                    <p class="wide dark">
                        A strategic roadmap tied to your goals.
                    </p>
                </div>

                <div class="w-100p f-col gap-8">
                    <span>Reliability</span>
                    <p class="wide dark">
                        Predictable check-ins, transparent progress reports, and on-time deliveries you can count on.
                    </p>
                </div>

                <div class="w-100p f-col gap-8">
                    <span>Confidence</span>
                    <p class="wide dark">
                        Proven expertise, data-backed decisions, and a risk-free guarantee that lets you focus on growth.
                    </p>
                </div>
        </div>
    </section>

        <section class="subsection">
            <h2 class="s5 botPad">
                You end up with a product that matches the roadmap you approved, delivered on schedule, and backed by proven expertise—so you can launch with confidence and focus on winning the market.
            </h2>
        </section>
    </div>

    {#if viewport.vw > 821}
        <ClearFolder />
    {/if}

</section>

<!-- 6 track record -->
<section class="f-col">
    <div class="f-col botPad">
        <span class="text-xs dark">{sectionLabels[4]}</span>
        <h1 class="s6">{sectionTitles[4]}</h1>

        <h2 class="s6">
            We amplify your runway and raise the odds of success.
        </h2>
    </div>
    
    <section class="subsection">

        <ul class="statHead f-row responsive nobullets">

            <li class="f-row">
                <div class="f-row align-cc">
                    <div class="statHead">$1M+</div>
                    <span class="statDesc">
                        Raised in funding by our clients.
                    </span>
                </div>
            </li>
            <li class="f-row">
                <div class="f-row align-cc">
                    <div class="statHead">$2M+</div>
                    <span class="statDesc">
                        Gross revenue generated by our products.
                    </span>
                </div>
            </li>
            <li class="f-row">
                <div class="f-row align-cc">
                    <div class="statHead">8/10</div>
                    <span class="statDesc">
                        Users would recommend our product to others.
                    </span>
                </div>
            </li>
            
        </ul>
        
    </section>
    
</section>

<!-- 7 Compare -->
<section>
<span class="text-xs dark">{sectionLabels[5]}</span>
<h1 class="s7 botPad">{sectionTitles[5]}</h1>

<table>
    <thead>
        <tr>
            <td></td>
            <td class="col1">East Europe dev shop</td>
            <td class="col2">India dev shop</td>
            <td class="col3">Partnering with us</td>
        </tr>
    </thead>
    <tbody>
        <tr>
            <td class="dark">Monthly cost</td>
            <td class="col1">$25,000</td>
            <td class="col2">$12,000</td>
            <td class="col3">$16,500</td>
        </tr>
        <tr>
            <td class="dark">Annual cost</td>
            <td class="col1">$300,000</td>
            <td class="col2">$144,000</td>
            <td class="col3">$96,000</td>
        </tr>
        <tr>
            <td class="dark">Two year cost</td>
            <td class="col1">$600,000</td>
            <td class="col2">$288,000</td>
            <td class="col3">Still $96,000</td>
        </tr>
        <tr>
            <td class="dark">Hidden costs</td>
            <td class="col2" colspan="2">Broken features, bug-fix churn, tech debt, sparse documentation, lackluster UI/UX</td>
            <td>We delivered on-time and within-budget.</td>
        </tr>
        <tr>
            <td class="dark">Result</td>
            <td class="col2" colspan="2">You continue the engagement out of hope for improvement, look for a different shop, or begin hiring internal resources.</td>
            <td>You’re well on your way to closing sales, securing funding, onboarding customers, and growing revenue.</td>
        </tr>
    </tbody>
</table>

<div class="pady-40">
        Example provided as generalized scenario comparison. We recognize that each project has different needs and the specific numbers and outcomes for your case may vary.</div>

<div class="subsection">
    <h1 class="s7b">The best part? We finish on time or your money back. Seriously.</h1>
    <h2 class="s7b">We stand behind our schedule. Complete the agreed‑upon scope within the timeline, or we’ll return the portion of the fee tied to missed milestones. IP and completed work remain yours.</h2>
</div>
</section>

<!-- 8 Case studies -->
<!-- <section>
<span class="text-xs dark">{sectionLabels[6]}</span>
<h1 class="s8">{sectionTitles[6]}</h1>
<div class="placeholder"></div>
</section> -->

<!-- 9 Conclusion -->
<section class="last f-col align-fs align2-fs gap-80"> 

    <div class="f-col">

        <span class="text-xs dark">{sectionLabels[7]}</span>
        <h1 class="last">{sectionTitles[7]}</h1>
        <h2 class="last">
            We can be the dark horse that wins you the race. Let’s build the best product possible together.
        </h2>
                <div class="f-row align-fs gap-16 padt-16">
            <span class="nowrap">
                Now booking for {month} '{year}
            </span> 
            <ToggleSwitch width={1} disabled={true} />
        </div>
    </div>

    <div class="w-100p f-col align-cc gap-28">
    
                <FancyButton 
                    text="Book a call"
                    click={'/booking'}
                />
                
                <TextButton 
                    text="How does our intro call work?" 
                    small={true}
                    arrowDir="upRight"
                    click={'/contact'}
                />
    </div>
</section>


<style>
/* !! NOTE: UNLIKE REST OF SITE, THESE STYLES ARE MOBILE FIRST !! */
section {
    z-index: 4;
    width: 100%;
    padding-top: var(--d18);
    padding-bottom: var(--d160);
    border-top: 1px solid #929292;
}

section .subsection {
    z-index: 4;
    padding-top: var(--d18);
    padding-bottom: var(--d160);
    border-top: 1px solid #3d3d3d;
}

h1 {
    z-index: 4;
    max-width: 100%;
    font-family: 'Instrument Sans', sans-serif;
    font-size: 2.625rem;
    line-height: 2.96rem;
    font-weight: 500;
    padding: .5rem 0;
}
    
h2 {
    z-index: 4;
    font-family: 'Instrument Sans', sans-serif;
    font-size: 2.125rem;
    line-height: 2.568rem;
    font-weight: 500;
    color: #c2c2c2;
    letter-spacing: -0.005rem;
}

div, span, p {
    z-index: 4;
    font-family: 'Inter Variable', sans-serif;
    font-size: 1.125rem;
    font-weight: 300;
}

.topPad {padding-top: var(--d16);}
.botPad {padding-bottom: var(--d60);}

.text-xs {
    z-index: 4;
    width: 100%;
    font-size: 1rem;
    font-weight: 300;
}

.dark {color: #929292;}
.light {color: #ffffff;}

ul {
    width: 100%;
    list-style-type: none;
    color: #929292;
}

li {
    padding-top: .125rem;
}

p {
    max-width: 100%;
    line-height: 1.465rem;
}

.multi {
    padding-top: 4.5rem;
    gap: 3rem;
}

.card {
    z-index: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    padding: 4rem 6.875rem 4.5rem 2.75rem;
    border-top: 1px solid rgba(255, 255, 255, 0.8);
    border-radius: .75rem;
    background: linear-gradient(
                    -140deg,
                    rgba(100, 100, 100, 0.2) 0%, 
                    rgba(15, 15, 15, .8) 100%
                    );
    backdrop-filter: blur(24px);
}

.multi.cards {
    gap: 1rem;
}

table {
    font-size: 1rem;
}

thead td, tbody td {
    padding: 1rem .375rem 3rem .375rem;
    vertical-align: top;
}

td.col1, td.col2 {
    width: 6.5rem;
}

thead td {border-top: 1px solid #3d3d3d;}
tbody td {border-top: 1px dashed #3d3d3d;}

thead td.col1, td.col2,
tbody td.col1, td.col2 {
    color: rgb(138, 138, 138);
}

td.dark {
    width: 7.5rem;
    padding-right: .25rem;
    color: #707070;
}

section.first {
    border: none;
}

h1.first {
    font-size: 4rem;
    line-height: 4.125rem;
    font-weight: 600;
}

section.first {
    padding-top: 4.5rem;
}

h1.s2 {width: 78%;}
h1.s3 {
    width: clamp(30rem, 2rem - 3vw, 80rem);
    max-width: 100%;
    padding-bottom: 3.5rem;
}
h1.s4 {width: 96%;}
h1.s6 {
    width: clamp(20rem, 2rem - 3vw, 30rem);
    max-width: 67%;
    padding-bottom: 1rem;
}
h1.s7 {
    width: clamp(20rem, 2rem - 3vw, 30rem);
    max-width: 65%;
}

h1.s7b {
    padding-bottom: 1.5rem;
}

li.first {color: white;}

h2.last {
    padding-bottom: 1.5rem;
}

ul.statHead {
    gap: 1.5rem;
}

div.statHead {
    width: clamp(10rem, 2rem + 2vw, 12rem);
    max-width: 26%;
}

ul.statHead li {
    width: clamp(30rem, 1rem + 3vw, 50rem);
    max-width: 100%;
    justify-content: center;
    align-items: center;
    padding: 0;
}

div.statHead {
    min-width: 0;
    width: 35%;
    padding-right: 1rem;
    color: white;
    font-size: 2.5rem;
    font-weight: 600;
}

span.statDesc {
    width: 100%;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    color: #929292;
}

div.placeholder {
    width: 100%;
    min-height: 800px;
    background: #252525;
}

@media (min-width: 768px) {

    .s3.multi {
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;
        max-width: 70%;
    }

    .s5.multi {
        padding-top: 0;
    }

    p.wide {
        max-width: 90%;
    }

    ul.statHead {
        flex-direction: column;
    }

    div.statHead {
        width: 20rem;
    }
}


@media (min-width: 821px) {

    div.subsection {
        width: 100%;
    }

    h1 {
        width: 65%;
        padding-top: .5rem;
        padding-bottom: 8rem;
        font-size: 52pt;
        line-height: 4.875rem;
        letter-spacing: -0.114rem;
    }

    h2 {
        font-weight: 300;
    }

    div, span, p {
        font-size: 1rem;
    }
    
    p {
        width: clamp(25%, 40rem - 12vw, 100rem);
        max-width: 42%;
        max-width: 100%;
    }

    ul {
        width: 100%;
    }

    li {
        font-weight: 300;
    }
    
    .text-xs {
        width: 24vw;
        font-size: 0.975rem;
        font-weight: 200;
    }

    .multi {
        width: 60%;
        padding-top: 0;
    }

    .multi.cards {
        width: 100%;
        justify-content: center;
    }   

    .card {
        width: 25%;
        min-height: 16rem;
        height: fit-content;
        padding: 
            clamp(2.5rem , .625rem + 0.45vw , 5rem) 
            clamp(2.25rem , .625rem + 0.45vw , 5rem); 
        backdrop-filter: blur(48px);
    }

    .card p {
        width: 100%;
    }

    p.wide {
        min-width: 0;
        width: clamp(3rem, 1.25rem + 12vw, 20.5rem);
        max-width: 100%;
    }

    section.first {
        padding: 8rem 0;
    }

    h1.first {
        width: clamp(30rem, 80rem + 20vw, 120rem);
        max-width: 100%;
        padding-bottom: 2rem;
        font-size: 6rem;
        line-height: 5.28rem;
        /* white-space: nowrap; */
    }
    h2.first {
        width: clamp(30rem, 60rem + 18vw, 120rem);
        max-width: 90%;
        color: #d7d7d7;
    }

    h1.s2 {width: 80%;}
    h2.s2 {
        margin-left: 24vw;
        width: clamp(30rem, 80rem - 8vw, 120rem);
        max-width: 92%;
    }

    h1.s3 {
        width: clamp(56rem, -120.1271rem - 20vw, 62rem);
        max-width: 86%;
        }

    .s3.multi {
        flex-direction: row;
        width: clamp(20rem, 40rem + 60vw, 140rem);
        max-width: 74%;
    }


    h1.s4 {
        width: clamp(56rem, 85.1271rem - 16vw, 68rem);
        max-width: 92%;
    }
    h1.s5 {
        width: clamp(56rem, 85.1271rem - 16vw, 68rem);
        max-width: 92%;
    }
    h2.s5 {
        min-width: 0;
        width: clamp(70rem, 272.271rem - 20vw, 100rem);
        max-width: 81%;
        padding-left: 24vw;
    }

    h1.s6 {
        width: clamp(30rem, 90rem - 8vw, 120rem);
        max-width: 80%;
        padding-bottom: 0;
    }

    h1.s7 {width: 100%;}
    h1.s7b, h2.s7b {padding-left: 24vw;}
    h1.s7b {
        width: clamp(36rem, 182.271rem - 10vw, 100rem);
        max-width: 90%;
        padding-bottom: 0;
    }
    h2.s7b {
        width: clamp(36rem, 182.271rem - 10vw, 100rem);
        max-width: 90%;
        padding-top: 1.5rem;
    }

    h1.s8 {width: 100%;}
    h1.last {width: 100%;}

    section.last {
        padding-bottom: 40rem;
        border-radius: 0 0 2.5rem 2.5rem;
        background: 
            conic-gradient(from 0deg at 103% 30%,
                hsla(0, 0%, 3%, 1),
                hsla(0, 0%, 3%, 1), 
                #7C83A9, 
                hsla(0, 0%, 3%, 1),
                hsla(0, 0%, 3%, 1)
                );
    }

    h1.last {
        padding-bottom: 0;
    }
    h2.last {
        min-width: 0;
        width: clamp(36rem, 182.271rem - 10vw, 50rem);
        max-width: 100%;
        padding-top: 1.5rem;
        
    }

    ul.statHead {
        padding: 0 10rem;
        justify-content: space-around;
        flex-direction: row;
    }

    ul.statHead li {
        gap: 1rem;
        align-items: flex-start;
    }

    div.statHead {
        min-width: 0;

        width: clamp(10rem, 2rem + 2vw, 18rem);
        max-width: max-content;

        padding: 0;
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        font-size: 2.75rem;
        font-weight: 600;
        color: white;
    }

    span.statDesc {
        padding-left: 1rem;
        font-size: 1.25rem;
    }

    table {
        width: 100%;
    }

    thead td, tbody td {
        padding: 1rem .375rem 6.125rem .375rem;
    }

    thead td {
        font-size: 1.625rem;
    }

    td.dark {
        font-weight: 300;
    }

    td.col1, td.col2, td.col3 {
        width: 10rem;
        font-size: 1.25rem;
    }

    td.dark {
        width: 12.5rem;
        padding-right: .75rem;
    }


}
</style>